import React, { MutableRefObject } from 'react';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from '../../../../../../api/connections.dao';
import { Popover } from '../../../../../global/popover/popover';
import Text from '../../../../../global/text/text';
import { getConnectionItemLabelText, getConnectionTemplateItemLabelText } from '../../../../../../infrastructure/utils/connections.utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { NO_CONNECTIONS_ADDED_TEXT } from '../connection-groups.texts';
import { IConnectionTemplateConnectionGroupDTO, TConnection } from '../../../../../../api/blueprints/connection.template.types';
import OptionsButton from '../../../../../global/options-button/options-button';
import { EDIT_TEXT, MORE_BUTTON_TEXT, PASSPORT_PREVIEW_TEXT } from '../../../../../../constants/common-labels.constants';
import { ConnectionGroupPassportPreviewPopover } from '../connection-groups-passport-preview/connection-group-passport-preview-popover';
import { ConnectionGroupsPassportPreviewViewModel } from '../connection-groups-passport-preview/connection-group-passport-preview.viewmodel';


export type TGroup = IConnectionGroupDTO | IConnectionTemplateConnectionGroupDTO;
export interface IConnectionGroupDetailsPopupProps {
    isPopoverOpen: boolean;
    editGroupPermission: boolean;
    closePopup: () => void;
    listItemRef: MutableRefObject<HTMLDivElement>;
    onEditGroup: (group: TGroup) => void;
    orderedConnections: (group: TGroup) => IConnectionDetailsDTO[] | TConnection[];
    group: TGroup;
    editButtonText: JSX.Element;
    connectionGroupsPassportPreviewViewModel: ConnectionGroupsPassportPreviewViewModel;
}

const ConnectionGroupDetailsPopup = ({
    isPopoverOpen,
    closePopup,
    listItemRef,
    onEditGroup,
    orderedConnections,
    editGroupPermission,
    group,
    connectionGroupsPassportPreviewViewModel,
}: IConnectionGroupDetailsPopupProps) => {

    const intl = useIntl();
    const optionsButton = [
        editGroupPermission && {
            label: intl.formatMessage(EDIT_TEXT),
            action: () => {
                onEditGroup(group);
            }
        },
        {
            label: intl.formatMessage(PASSPORT_PREVIEW_TEXT),
            action: connectionGroupsPassportPreviewViewModel.openPopover
        },
    ].filter(Boolean);

    return (
        <>
            <Popover
                isOpen={isPopoverOpen}
                onClickOutside={closePopup}
                customWidth={225}
                padding={0}
                align='start'
                buttonRef={listItemRef}
            >
                <div className='w-100 connection-list__popover connection-group-item-popover'>
                    <ConnectionGroupDetailsPopupContent connections={orderedConnections(group)} />
                    <OptionsButton
                        className='small-button'
                        label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                        options={optionsButton}
                        position='top'
                        align='start'
                    />

                </div>
            </Popover>
            <ConnectionGroupPassportPreviewPopover
                vm={connectionGroupsPassportPreviewViewModel}
            />
        </>
    );
};

export interface IConnectionGroupDetailsPopupContentProps {
    connections: IConnectionDetailsDTO[] | TConnection[];
}

export const ConnectionGroupDetailsPopupContent = ({
    connections
}: IConnectionGroupDetailsPopupContentProps) => {

    if (!connections.length) {
        return <FormattedMessage {...NO_CONNECTIONS_ADDED_TEXT} />;
    }

    const isMeetingConnection = (connections[0] as IConnectionDetailsDTO).meetingConnectionId;
    const composeMessage = isMeetingConnection ? getConnectionItemLabelText : getConnectionTemplateItemLabelText;

    return (
        <>
            {connections.map(c => {
                return (
                    <Text
                        key={c.meetingConnectionId ?? c.connectionId}
                        tag='div'
                        className='m-bottom-10px font-size-13px'
                        withEllipsis
                        message={composeMessage(c)}
                    />
                );
            })
            }
        </>
    );
};

export default ConnectionGroupDetailsPopup;
